import React, { useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import { setCheckoutStep, setTip } from "@store/reducers/cart";

import styled, { keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const ShakeWrap = styled.div<{ $shake: boolean }>`
  animation: ${({ $shake }) => ($shake ? shake : "none")} 0.5s ease;
`;

import {
  Body,
  BottomFade,
  DesktopHeader,
  FirstSectionLabel,
  FooterActions,
  PhoneHeader,
  ScrollArea,
  ScrollChevron,
  ScrollHint,
  useScrollAffordance,
} from "./phoneTemplate";
import {
  BackBtn,
  CheckboxBox,
  IconCircle,
  PrimaryBtn,
  SectionLabel,
  SkipRow,
  SlidePanelFooter,
  StepSubtitle,
  StepTitle,
  TipCard,
  TipCustomStepper,
  TipCustomValue,
  TipGrid,
  TipLabel,
  TipPercent,
  TipStepperBtn,
  TotalAmount,
  TotalRow,
  TotalsWrap,
} from "./styles";

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z"
      stroke="#ebc28e"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6L5 9L10 3" stroke="#3c2d23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type TipOption = "15" | "20" | "30" | "custom" | "skip" | null;

const TipView: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, tipType, tipAmount, isLoggedInCheckout } = useAppSelector((state) => state.cart);
  const user = useAppSelector((state) => state.user);
  const isAuthenticated = !!(user as any)?.user?.email;

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const [selected, setSelected] = useState<TipOption>(() => {
    if (tipType === "skip") return "skip";
    if (tipType === "custom") return "custom";
    if (tipType === "percent") {
      const pct = Math.round((tipAmount / subtotal) * 100);
      if (pct === 15) return "15";
      if (pct === 20) return "20";
      if (pct === 30) return "30";
      return "custom";
    }
    return null;
  });

  const [shakeTip, setShakeTip] = useState(false);

  const triggerShake = () => {
    setShakeTip(true);
    setTimeout(() => setShakeTip(false), 500);
  };

  const [customAmount, setCustomAmount] = useState(() => {
    if (tipType === "custom") return tipAmount;
    return 2;
  });

  const getTipAmount = (): number => {
    if (selected === "skip" || selected === null) return 0;
    if (selected === "custom") return customAmount;
    return subtotal * (parseInt(selected) / 100);
  };

  const currentTip = getTipAmount();
  const total = subtotal + currentTip;

  const handleSelect = (option: TipOption) => {
    setSelected(option);
  };

  const holdRef = useRef<NodeJS.Timer>(null);
  // Once touch is used, ignore the synthetic mouse events iOS fires on release
  // (touchstart → … → mousedown/up) so the hold doesn't double-fire / add an
  // extra step. Touch devices never get a real mouse, so this stays true.
  const usingTouchRef = useRef(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef);

  const handleCustomStep = (change: number) => {
    setCustomAmount((prev) => Math.max(1, prev + change));
  };

  const startHold = (change: number) => {
    if (holdRef.current) return; // already holding — ignore re-entry
    handleCustomStep(change);
    const delay = setTimeout(() => {
      holdRef.current = setInterval(() => handleCustomStep(change), 100);
    }, 400);
    holdRef.current = delay as unknown as NodeJS.Timer;
  };

  // Hold handlers wired to BOTH mouse and touch so press-and-hold-to-repeat
  // works on phone/tablet, not just desktop.
  const holdMouse = (change: number) => () => { if (!usingTouchRef.current) startHold(change); };
  const holdTouch = (change: number) => () => { usingTouchRef.current = true; startHold(change); };

  const stopHold = () => {
    if (holdRef.current) {
      clearTimeout(holdRef.current as unknown as NodeJS.Timeout);
      clearInterval(holdRef.current);
      holdRef.current = null;
    }
  };

  const handleContinue = () => {
    if (selected === null) {
      triggerShake();
      return;
    }
    if (selected === "skip") {
      dispatch(setTip({ type: "skip", amount: 0 }));
    } else if (selected === "custom") {
      dispatch(setTip({ type: "custom", amount: customAmount }));
    } else {
      dispatch(setTip({ type: "percent", amount: currentTip }));
    }
    dispatch(setCheckoutStep("review"));
  };

  const handleBack = () => {
    if (isAuthenticated || isLoggedInCheckout) {
      // Account step is skipped for authenticated users — Back from Tip goes to Cart.
      dispatch(setCheckoutStep("cart"));
    } else {
      dispatch(setCheckoutStep("billing"));
    }
  };

  return (
    <>
      <PhoneHeader>
        <h2>Tip The Artist</h2>
      </PhoneHeader>

      <ScrollArea>
      <Body ref={bodyRef}>
      <DesktopHeader>
        <IconCircle>
          <HeartIcon />
        </IconCircle>
        <StepTitle>Tip The Artist</StepTitle>
        <StepSubtitle>Show love to the creator of your chosen music</StepSubtitle>
      </DesktopHeader>

      <FirstSectionLabel>
        <span>Select Amount</span>
      </FirstSectionLabel>

      <ShakeWrap $shake={shakeTip}>
        <TipGrid>
          <TipCard $active={selected === "15"} onClick={() => handleSelect("15")}>
            <TipPercent>15%</TipPercent>
            <TipLabel>${(subtotal * 0.15).toFixed(2)}</TipLabel>
          </TipCard>
          <TipCard $active={selected === "20"} onClick={() => handleSelect("20")}>
            <TipPercent>20%</TipPercent>
            <TipLabel>${(subtotal * 0.2).toFixed(2)}</TipLabel>
          </TipCard>
          <TipCard $active={selected === "30"} onClick={() => handleSelect("30")}>
            <TipPercent>30%</TipPercent>
            <TipLabel>${(subtotal * 0.3).toFixed(2)}</TipLabel>
          </TipCard>
          <TipCard $active={selected === "custom"} onClick={() => handleSelect("custom")}>
            {selected === "custom" ? (
              <>
                <TipPercent style={{ color: "#ffffff" }}>Custom</TipPercent>
                <TipCustomStepper onClick={(e) => e.stopPropagation()}>
                  <TipStepperBtn onMouseDown={holdMouse(-1)} onMouseUp={stopHold} onMouseLeave={stopHold} onTouchStart={holdTouch(-1)} onTouchEnd={stopHold} onTouchCancel={stopHold} style={{ paddingBottom: "1px", paddingLeft: "1px" }}>-</TipStepperBtn>
                  <TipCustomValue>${customAmount.toFixed(2)}</TipCustomValue>
                  <TipStepperBtn onMouseDown={holdMouse(1)} onMouseUp={stopHold} onMouseLeave={stopHold} onTouchStart={holdTouch(1)} onTouchEnd={stopHold} onTouchCancel={stopHold} style={{ paddingLeft: "1px" }}>+</TipStepperBtn>
                </TipCustomStepper>
              </>
            ) : (
              <>
                <TipPercent>Custom</TipPercent>
                <TipLabel>Set your own</TipLabel>
              </>
            )}
          </TipCard>
        </TipGrid>

        <SectionLabel>
          <span>Or</span>
        </SectionLabel>

        <SkipRow onClick={() => handleSelect(selected === "skip" ? null : "skip")}>
          <CheckboxBox $checked={selected === "skip"} style={{ position: "relative", top: "-1px" }}>
            <CheckIcon />
          </CheckboxBox>
          <span>Continue without tip</span>
        </SkipRow>
      </ShakeWrap>

      <TotalsWrap>
        <TotalRow>
          <span>Subtotal</span>
          <TotalAmount>${subtotal.toFixed(2)}</TotalAmount>
        </TotalRow>
        <TotalRow>
          <span>Tip</span>
          <TotalAmount>${currentTip.toFixed(2)}</TotalAmount>
        </TotalRow>
        <TotalRow $bold>
          <span>Total</span>
          <TotalAmount $bold>${total.toFixed(2)}</TotalAmount>
        </TotalRow>
      </TotalsWrap>
      </Body>
      <BottomFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </ScrollArea>

      <SlidePanelFooter>
        <FooterActions>
          <BackBtn onClick={handleBack}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg> Back</BackBtn>
          <PrimaryBtn
            onClick={handleContinue}
            style={selected === null ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
          >
            <ButtonLabel glyph="arrowRight" muted={selected === null}>Continue</ButtonLabel>
          </PrimaryBtn>
        </FooterActions>
      </SlidePanelFooter>
    </>
  );
};

export default TipView;
